import { Check, CopyAll, ExpandLess, ExpandMore } from '@mui/icons-material'
import { marked } from 'marked'
import React, { memo, useState } from 'react'
import Markdown from 'react-markdown'
import { getCustomerConfig } from '../hooks/useCustomerConfig'
import { Message } from '../types'
import { Button } from './Buttons'
import { SourcesView } from './SourcesView'

type ExpandablePanelProps = {
  title: React.ReactNode
  children: React.ReactNode
  variant?: 'normal' | 'quotation' | 'blue'
}

const snakeToSentence = (snakeCasedString: string) =>
  snakeCasedString.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase())

export const ExpandablePanel = ({
  title,
  children,
  variant,
}: ExpandablePanelProps) => {
  const [isExpanded, setIsExpanded] = useState(false)

  const toggleExpand = () => {
    setIsExpanded(!isExpanded)
  }

  const headerStyles = {
    normal: 'p-1 bg-transparent text-gray-500 dark:text-white dark:bg-gray-800',
    blue: 'p-2 text-blue-700 bg-blue-50 shadow border dark:bg-blue-800 dark:border-blue-600 dark:text-white',
    quotation:
      'p-2 bg-white bg-gray-100 shadow border bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-white',
  }

  return (
    <div
      className={
        'flex flex-col rounded ' +
        (isExpanded
          ? 'bg-white border dark:bg-gray-700 dark:border-gray-600'
          : 'opacity-80 hover:opacity-100')
      }
    >
      <div
        className={`cursor-pointer flex justify-between items-center gap-1 ${headerStyles[variant || 'normal']}`}
        onClick={toggleExpand}
      >
        <div className="flex-1">{title}</div>
        <div className="text-xs text-gray-500 dark:text-white">
          {isExpanded ? <ExpandLess /> : <ExpandMore />}
        </div>
      </div>
      {isExpanded && <div className={`overflow-hidden p-3`}>{children}</div>}
    </div>
  )
}

const CustomAnchor = (props: any) => {
  return props.href.match(/^https?:\/\//) ? (
    <a
      {...props}
      href={props.href}
      target="_blank"
      rel="noreferrer"
      className={'text-blue-800 dark:text-blue-300'}
    >
      {props.children}
    </a>
  ) : (
    <a
      {...props}
      className={'text-fuchsia-800 p-1 no-underline align-top text-xs'}
    >
      {props.children}
    </a>
  )
}

const markdownComponents = { a: CustomAnchor }

/**
 * Memoized component for rendering a single chat message.
 * Prevents React from re-rendering (and reconciling the DOM of) messages
 * that haven't changed. This avoids crashes when browser extensions like
 * Google Translate mutate the DOM of already-rendered messages.
 */
const ChatMessage = memo(({ message }: { message: Message }) => {
  const text = message.text

  return (
    <div className={'flex flex-col gap-2'}>
      <ChatBubble
        color={message.role === 'assistant' ? 'transparent' : 'fancy'}
      >
        {message.steps && message.steps.length > 0 && (
          <div className={'flex flex-col gap-2'}>
            {message.steps.map((step, stepIndex) => {
              const isInProgress =
                stepIndex === message.steps!.length - 1 && message.text === ''
              return (
                <ExpandablePanel
                  key={stepIndex}
                  variant={'normal'}
                  title={
                    <div className={'flex gap-2 justify-between'}>
                      <div className={'flex items-center gap-2'}>
                        {isInProgress ? (
                          <BotThinkingSpinner />
                        ) : (
                          <BotDoneThinking />
                        )}
                        {step.sentence ? (
                          <span>{step.sentence}</span>
                        ) : (
                          <div className={'flex items-center gap-2'}>
                            {snakeToSentence(step.name)}
                          </div>
                        )}
                      </div>
                      <div className={'flex items-center gap-2 text-sm'}>
                        {step.documents ? step.documents.length + ' treff' : ''}
                      </div>
                    </div>
                  }
                >
                  <SourcesView
                    documents={step.documents || []}
                    highlightedDocumentIds={[]}
                  />
                  {step.sentence == null && <Dict dict={step.args} />}
                </ExpandablePanel>
              )
            })}
          </div>
        )}
        {message.facts && (
          <div className={'flex flex-col gap-1 mt-2'}>
            {message.facts.map((fact, factIndex) => {
              const sourceUrl = fact.source_document_url

              const documentsContent = (message.documents || []).filter(
                (doc) => (doc.source as string).indexOf(sourceUrl) > -1,
              )

              return (
                <ExpandablePanel
                  variant={'normal'}
                  key={factIndex}
                  title={
                    <div className={'flex flex-col gap-1 text-sm'}>
                      <span>
                        Nøkkelfakta fra{' '}
                        <a
                          className={
                            'text-blue-700 underline dark:text-blue-300'
                          }
                          href={sourceUrl}
                        >
                          {sourceUrl}
                        </a>
                        :
                      </span>
                      {fact.exact_quotes.map((e: any, quoteIndex: number) => (
                        <div
                          key={quoteIndex}
                          className={
                            'p-2 bg-gray-200 dark:bg-black dark:bg-opacity-30 ml-4 rounded'
                          }
                        >
                          {e}
                        </div>
                      ))}
                    </div>
                  }
                >
                  <div className={'text-sm'}>
                    Fra <a href={sourceUrl}>{sourceUrl}</a>:
                  </div>
                  {documentsContent.map((doc, docIndex) => (
                    <div
                      key={docIndex}
                      className={
                        'border-l-8 bg-gray-50 p-2 text-sm prose lg:prose prose-stone dark:prose-invert dark:bg-gray-600 dark:text-white'
                      }
                    >
                      <Markdown>{doc.content}</Markdown>
                    </div>
                  ))}
                </ExpandablePanel>
              )
            })}
          </div>
        )}
        <div className={'prose lg:prose prose-stone dark:prose-invert'}>
          <Markdown components={markdownComponents}>{text}</Markdown>
        </div>
        {message.role === 'assistant' && (
          <div className={'mt-4 p-2 flex gap-2'}>
            <CopyResponseButton text={text} />
          </div>
        )}
      </ChatBubble>

      {message.citations && (
        <div className={'flex flex-col gap-2'}>
          {message.citations.map((citation, citationIndex) => (
            <blockquote
              key={citationIndex}
              className={'p-2 bg-gray-100 border-l-8 flex flex-col gap-2'}
            >
              <div className={'font-bold'}>
                {citation.factual_statement_from_text}
              </div>
              <div>{citation.source_document_id}</div>
              <div>{citation.exact_quote}</div>
            </blockquote>
          ))}
        </div>
      )}
    </div>
  )
})

type Props = {
  conversation: Message[]
  emptyConversationPlaceholder: React.ReactNode
}
export const Chat = ({ conversation, emptyConversationPlaceholder }: Props) => {
  const waitingForBotToRespond =
    conversation.length > 0 &&
    conversation[conversation.length - 1].role === 'user'
  const conversationEmpty = conversation.length === 0

  return (
    <div
      className={
        'overflow-y-auto flex-1 bg-gray-100  dark:bg-gray-800 dark:text-white rounded-t-3xl flex flex-col items-center ' +
        (conversationEmpty ? 'justify-center' : '')
      }
    >
      <div className="p-4 flex flex-col gap-4 lg:max-w-5xl w-full">
        {conversationEmpty && emptyConversationPlaceholder}
        {conversation.map((message, index) => (
          <ChatMessage key={index} message={message} />
        ))}

        {waitingForBotToRespond && (
          <ChatBubble color={'transparent'}>
            <div className={'flex gap-3 items-center'}>
              <BotThinkingSpinner />
              <div>Assistenten tenker...</div>
            </div>
          </ChatBubble>
        )}
      </div>
    </div>
  )
}

const Dict = ({ dict }: { dict: object }) => {
  return (
    <div
      className={
        'flex flex-col gap-1 bg-white dark:bg-black bg-opacity-20 dark:bg-opacity-20 p-1 border border-black dark:border-white border-opacity-20 dark:border-opacity-20 shadow rounded-lg'
      }
    >
      {Object.entries(dict).map(([key, value]) => (
        <div key={key} className={'flex gap-2 items-center'}>
          <div className={'font-bold text-xs uppercase'}>{key}</div>
          <div>
            {Array.isArray(value) ? (
              <ul className={''}>
                {value.map((item, idx) => (
                  <li key={idx}>– {item}</li>
                ))}
              </ul>
            ) : (
              <div>
                {typeof value === 'object' ? <Dict dict={value} /> : value}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

const BotThinkingSpinner = () => (
  <div
    className={
      'animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-700 dark:border-blue-200'
    }
  ></div>
)

const BotDoneThinking = () => (
  <div className={''}>
    <Check fontSize={'small'} />
  </div>
)

// Configure the marked renderer to avoid <p> tags
const customMarkdownRenderer = new marked.Renderer()
customMarkdownRenderer.paragraph = (paragraph) =>
  `${customMarkdownRenderer.parser.parseInline(paragraph.tokens)}\n\n`
const markdownToHtml = (markdown: string) =>
  (marked(markdown, { renderer: customMarkdownRenderer }) as string).trim()

// Convert markdown to plain text by first converting to HTML, then stripping all HTML tags
const getPlainText = (markdown: string) => {
  // Use marked with default renderer (simpler HTML than our custom renderer)
  const html = marked.parse(markdown) as string

  // Then create a temporary DOM element to parse the HTML
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = html

  // Extract text content (browsers handle all HTML tag stripping)
  const plainText = tempDiv.textContent || tempDiv.innerText || ''

  // Clean up whitespace
  return plainText.replace(/\n{3,}/g, '\n\n').trim()
}

export const CopyResponseButton = ({ text }: { text: string }) => {
  const [isCopied, setIsCopied] = useState(false)
  const config = getCustomerConfig()
  const format = config?.copyButtonFormat || 'html'

  const copyResponse = () => {
    let textToCopy: string

    switch (format) {
      case 'plain':
        textToCopy = getPlainText(text)
        break
      case 'markdown':
        textToCopy = text
        break
      case 'html':
      default:
        textToCopy = markdownToHtml(text) as string
        break
    }

    navigator.clipboard.writeText(textToCopy).then(() => {
      setIsCopied(true)
      setTimeout(() => setIsCopied(false), 2000) // Reset after 2 seconds
    })
  }

  return (
    <Button onClick={copyResponse}>
      {isCopied ? <Check /> : <CopyAll />}
      {isCopied ? 'Kopiert!' : 'Kopier svar'}
    </Button>
  )
}

type ChatBubbleProps = {
  children: React.ReactNode
  color: 'transparent' | 'fancy'
}
export const ChatBubble = (p: ChatBubbleProps) => {
  const bubbleClasses = {
    // User message must pad to the right, and have a chat bubble arrow on the right
    transparent: 'rounded-r-lg pr-4 mr-auto',
    fancy: `bg-fancy-pink dark:bg-none dark:bg-gray-700 dark:border-gray-600 rounded-r-lg pr-4 ml-auto border-2`,
  }
  return (
    <div
      className={`flex flex-col gap-4 font-poppins p-3 rounded-lg ${bubbleClasses[p.color]}`}
    >
      {p.children}
    </div>
  )
}
